<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Eye, RotateCcw, Search, Star } from '@lucide/vue'
import {
  Badge, Button, EmptyState, FormField, Input, Modal, PageHeader, Pagination, Section, Select, Spinner, Table, Toolbar, useToast,
} from '@drama/ui'

import { listNovelPacks, setNovelPackFeatured } from '../../api/novel/packs.js'
import { useNovelPageText } from '../../components/novel/novel-page-text.js'
import PackListingAction from '../../components/novel/packs/PackListingAction.vue'
import PackPreview from '../../components/novel/packs/PackPreview.vue'
import NovelReviewQueue from '../../components/novel/packs/NovelReviewQueue.vue'
import NovelDramaAssets from '../../components/novel/packs/NovelDramaAssets.vue'
import { STATUS_VARIANT, VALIDATION_VARIANT, isListedInvalid, packStatusKey } from '../../components/novel/packs/pack-status.js'

const NS = 'visualGameAdmin.novelPacks'
const STATUSES = ['draft', 'published', 'unpublished', 'archived', 'deleted']
defineOptions({ name: 'NovelPacksPage' })

const { t } = useI18n()
const toast = useToast()
const { apiError, apiErrorLine, dateTime } = useNovelPageText()
const tp = (key, params) => t(`${NS}.${key}`, params ?? {})

const loading = ref(false)
const error = ref(null)
const items = ref([])
const total = ref(0)
const page = ref(1)
const pages = ref(1)
const pageSize = ref(20)
// q 由服务端按标题、slug、创作者邮箱模糊匹配。
const filters = reactive({ status: '', q: '' })
// 正在预览的作品 id；空串表示预览关着。
const previewId = ref('')
const dramaTarget = ref(null)

const statusOptions = computed(() => [
  { value: '', label: tp('filters.allStatuses') },
  ...STATUSES.map((value) => ({ value, label: t(packStatusKey(value)) })),
])

const columns = computed(() => [
  { key: 'title', label: tp('table.title'), width: '200px' },
  { key: 'owner_email', label: tp('table.owner'), width: '170px' },
  { key: 'latest_version', label: tp('table.latestVersion'), width: '110px', mono: true },
  { key: 'status', label: tp('table.status'), width: '90px' },
  { key: 'validation_status', label: tp('table.validation'), width: '130px' },
  { key: 'featured_rank', label: tp('table.featured'), width: '80px' },
  { key: 'updated_at', label: tp('table.updatedAt'), width: '160px' },
  { key: 'actions', label: t('visualGameAdmin.common.actions'), width: '330px', align: 'right', fixed: 'right', wrap: true },
])

const summary = computed(() => tp('pager.summary', { total: total.value, page: page.value, pages: pages.value }))

onMounted(() => load())

/** @param {boolean} [silent] 动作之后的刷新不切换成整页加载态。 */
async function load(silent = false) {
  if (!silent) loading.value = true
  error.value = null
  try {
    const data = await listNovelPacks({
      page: page.value,
      page_size: pageSize.value,
      status: filters.status || undefined,
      q: filters.q.trim() || undefined,
    })
    items.value = data.items
    total.value = data.total
    pages.value = Math.max(data.pages, 1)
    page.value = data.page
  } catch (requestError) {
    error.value = apiError(requestError)
  } finally {
    loading.value = false
  }
}

function applyFilters() { page.value = 1; load() }
function resetFilters() { filters.status = ''; filters.q = ''; applyFilters() }
function prevPage() { page.value -= 1; load() }
function nextPage() { page.value += 1; load() }
function changePageSize(size) { pageSize.value = size; applyFilters() }

/** @param {'published'|'unpublished'} action */
async function onListingDone(row, action) {
  toast.success(tp(`toast.${action}`, { title: row.title }))
  await load(true)
}

// ------------------------------------------------------------------ 精选序

const featuredOpen = ref(false)
const featuredSaving = ref(false)
const featuredTarget = ref(null)
const featuredText = ref('')

/** 空串以外只收非负整数；返回 undefined 表示输入不合法。 */
const featuredRank = computed(() => {
  const text = String(featuredText.value).trim()
  return /^\d+$/.test(text) ? Number(text) : undefined
})
const featuredInvalid = computed(() => featuredText.value !== '' && featuredRank.value === undefined)

function openFeatured(row) {
  featuredTarget.value = row
  featuredText.value = row.featured_rank === null ? '' : String(row.featured_rank)
  featuredOpen.value = true
}

async function saveFeatured(rank) {
  const row = featuredTarget.value
  featuredSaving.value = true
  try {
    await setNovelPackFeatured(row.id, { featured_rank: rank })
    featuredOpen.value = false
    toast.success(tp(rank === null ? 'toast.featuredCleared' : 'toast.featuredSet', { title: row.title }))
    await load(true)
  } catch (requestError) {
    toast.error(apiErrorLine(requestError), { duration: 0 })
  } finally {
    featuredSaving.value = false
  }
}
</script>

<template>
  <div
    class="np vga-page"
    data-testid="novel-packs-page"
    data-insp-path="apps/visual-game-admin/src/pages/novel/Packs.vue"
  >
    <PageHeader :title="tp('title')" />
    <NovelReviewQueue @published="load(true)" />

    <Section>
      <Toolbar>
        <div class="drama-toolbar-field">
          <label>{{ tp('filters.status') }}</label>
          <Select
            v-model="filters.status"
            :options="statusOptions"
            size="sm"
            :aria-label="tp('filters.status')"
            data-testid="novel-packs-status-filter"
          />
        </div>
        <div class="drama-toolbar-field drama-toolbar-field--wide">
          <label>{{ tp('filters.search') }}</label>
          <Input
            v-model="filters.q"
            type="search"
            size="sm"
            :placeholder="tp('filters.searchPlaceholder')"
            data-testid="novel-packs-search"
            @keyup.enter="applyFilters"
          />
        </div>
        <template #actions>
          <Button
            size="sm"
            :loading="loading"
            data-testid="novel-packs-apply"
            @click="applyFilters"
          >
            <Search :size="14" /> {{ tp('filters.apply') }}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            :disabled="loading"
            data-testid="novel-packs-reset"
            @click="resetFilters"
          >
            <RotateCcw :size="14" /> {{ t('visualGameAdmin.common.reset') }}
          </Button>
        </template>
      </Toolbar>
    </Section>

    <div
      v-if="loading"
      class="vga-state"
    >
      <Spinner /><span>{{ t('visualGameAdmin.common.loading') }}</span>
    </div>
    <div
      v-else-if="error"
      class="vga-state"
    >
      <p
        class="vga-error"
        :title="error.title"
      >
        {{ error.text }}
      </p>
      <Button
        variant="secondary"
        @click="load()"
      >
        {{ t('visualGameAdmin.common.retry') }}
      </Button>
    </div>
    <template v-else>
      <Section :padded="false">
        <EmptyState
          v-if="items.length === 0"
          :title="tp('emptyTitle')"
        />
        <Table
          v-else
          :columns="columns"
          :rows="items"
          data-testid="novel-packs-table"
        >
          <template #title="{ row }">
            <div class="np__title">
              <span class="np__name">{{ row.title }}</span>
              <span class="np__slug">{{ row.slug }}<template v-if="row.language"> · {{ row.language }}</template></span>
            </div>
          </template>
          <template #owner_email="{ row }">
            {{ row.owner_email || '—' }}
          </template>
          <template #latest_version="{ row }">
            <span v-if="row.latest_version">{{ row.latest_version }}</span>
            <span
              v-else
              class="np__muted"
            >{{ tp('noVersion') }}</span>
          </template>
          <template #status="{ row }">
            <Badge
              size="sm"
              :variant="STATUS_VARIANT[row.status]"
            >
              {{ t(packStatusKey(row.status)) }}
            </Badge>
          </template>
          <template #validation_status="{ row }">
            <div class="np__check">
              <Badge
                size="sm"
                :variant="VALIDATION_VARIANT[row.validation_status]"
              >
                {{ tp(`validation.${row.validation_status}`) }}
              </Badge>
              <span
                v-if="isListedInvalid(row)"
                class="np__risk"
                :title="tp('listedInvalidHint')"
                :data-testid="`novel-pack-listed-invalid-${row.id}`"
              >{{ tp('listedInvalid') }}</span>
            </div>
          </template>
          <template #featured_rank="{ row }">
            <Badge
              v-if="row.featured_rank !== null"
              size="sm"
              variant="info"
              :data-testid="`novel-pack-featured-${row.id}`"
            >
              #{{ row.featured_rank }}
            </Badge>
            <span
              v-else
              class="np__muted"
            >{{ tp('notFeatured') }}</span>
          </template>
          <template #updated_at="{ row }">
            {{ dateTime(row.updated_at) }}
          </template>
          <template #actions="{ row }">
            <div class="np__actions">
              <Button
                variant="ghost"
                size="sm"
                :data-testid="`novel-pack-preview-${row.id}`"
                @click="previewId = row.id"
              >
                <Eye :size="14" /> {{ tp('actions.preview') }}
              </Button>
              <Button
                v-if="row.status !== 'deleted'"
                variant="ghost"
                size="sm"
                :data-testid="`novel-pack-drama-assets-${row.id}`"
                @click="dramaTarget = row"
              >
                {{ t('novelDramaAdmin.assets.action') }}
              </Button>
              <PackListingAction
                :pack="row"
                @done="onListingDone(row, $event)"
              />
              <Button
                v-if="row.status !== 'deleted'"
                variant="ghost"
                size="sm"
                :aria-label="tp('actions.featured')"
                :title="tp('actions.featured')"
                :data-testid="`novel-pack-featured-open-${row.id}`"
                @click="openFeatured(row)"
              >
                <Star :size="14" />
              </Button>
            </div>
          </template>
        </Table>
      </Section>
      <Pagination
        :has-prev="page > 1"
        :has-next="page < pages"
        :loading="loading"
        :summary="summary"
        :prev-label="tp('pager.prev')"
        :next-label="tp('pager.next')"
        :page-size="pageSize"
        :page-size-options="[20, 50, 100]"
        :page-size-label="tp('pager.pageSize')"
        @prev="prevPage"
        @next="nextPage"
        @update:page-size="changePageSize"
      />
    </template>

    <Modal
      :open="featuredOpen"
      :title="tp('featuredForm.title')"
      :close-on-backdrop="!featuredSaving"
      width="480px"
      @update:open="featuredOpen = $event"
    >
      <form
        class="np__form"
        @submit.prevent="featuredRank !== undefined && saveFeatured(featuredRank)"
      >
        <p
          v-if="featuredTarget"
          class="np__target"
        >
          {{ featuredTarget.title }}
        </p>
        <FormField
          :label="tp('featuredForm.rank')"
          :hint="tp('featuredForm.hint')"
          :error="featuredInvalid ? tp('featuredForm.invalid') : ''"
        >
          <Input
            v-model="featuredText"
            type="number"
            :invalid="featuredInvalid"
            block
            data-testid="novel-pack-featured-input"
          />
        </FormField>
      </form>
      <template #footer>
        <Button
          variant="ghost"
          :disabled="featuredSaving"
          @click="featuredOpen = false"
        >
          {{ t('visualGameAdmin.common.cancel') }}
        </Button>
        <Button
          v-if="featuredTarget && featuredTarget.featured_rank !== null"
          variant="secondary"
          :loading="featuredSaving"
          data-testid="novel-pack-featured-clear"
          @click="saveFeatured(null)"
        >
          {{ tp('featuredForm.clear') }}
        </Button>
        <Button
          variant="primary"
          :loading="featuredSaving"
          :disabled="featuredRank === undefined"
          data-testid="novel-pack-featured-save"
          @click="saveFeatured(featuredRank)"
        >
          {{ tp('featuredForm.save') }}
        </Button>
      </template>
    </Modal>

    <PackPreview
      :pack-id="previewId"
      @close="previewId = ''"
    />
    <NovelDramaAssets
      :pack-id="dramaTarget?.id || ''"
      :pack-title="dramaTarget?.title || ''"
      @close="dramaTarget = null"
    />
  </div>
</template>

<style scoped>
.np__title { display: grid; gap: 2px; min-width: 0; }
.np__name { font-weight: var(--drama-font-weight-medium); overflow: hidden; text-overflow: ellipsis; }
.np__slug { font-size: var(--drama-font-size-xs, 12px); color: var(--drama-color-text-muted); overflow: hidden; text-overflow: ellipsis; }
.np__muted { color: var(--drama-color-text-muted); }
.np__check { display: grid; gap: var(--drama-space-xs); justify-items: start; }
.np__risk { color: var(--drama-color-danger); font-size: var(--drama-font-size-xs, 12px); }
.np__actions { display: flex; flex-wrap: wrap; justify-content: flex-end; align-items: center; gap: var(--drama-space-xs); }
.np__form { display: grid; gap: var(--drama-space-md); }
.np__target { margin: 0; font-weight: var(--drama-font-weight-medium); }
</style>
